import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Calendar } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { Seo } from "@/components/Seo";
import { ProjectCard } from "@/components/ProjectCard";
import { CTASection } from "@/components/CTASection";
import { ImageCarousel } from "@/components/ImageCarousel";
import { resolveImage } from "@/lib/api";
import api from "@/lib/api";

const CATEGORIES = ["All", "Residential", "Commercial", "Waterfront", "Land Development"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <PublicLayout>
      <Seo title="Completed Projects" description="Explore completed projects by Crawford Construction & Development Ltd. — waterfront homes, docks, seawalls, boathouses, kitchens and commercial builds across Belize." />
      <section className="pt-40 pb-16 bg-navy">
        <div className="max-w-7xl mx-auto px-5">
          <p className="text-xs sm:text-sm tracking-[0.25em] uppercase font-bold text-gold mb-5">Portfolio</p>
          <h1 className="font-display text-5xl sm:text-6xl text-white leading-tight">Completed projects.</h1>
          <p className="mt-6 text-white/70 text-lg max-w-2xl">A selection of homes, waterfront structures and developments we've delivered across Belize.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex flex-wrap gap-3 mb-12">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`px-6 py-2.5 text-sm font-semibold tracking-wide transition-colors border ${
                  filter === c ? "bg-navy text-white border-navy" : "bg-white text-navy border-gray-200 hover:border-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-20 flex justify-center"><div className="h-10 w-10 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
          ) : filtered.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <ProjectCard key={p.id} project={p} index={i} onClick={() => setActive(p)} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center text-muted-foreground">No projects in this category yet.</div>
          )}
        </div>
      </section>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-navy/90 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setActive(null)}
            data-testid="project-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="relative">
                <ImageCarousel images={active.image_paths || []} captions={active.captions || []} title={active.title} />
                <button onClick={() => setActive(null)} data-testid="close-modal-btn" className="absolute top-4 right-4 z-10 bg-white/90 p-2 hover:bg-gold transition-colors">
                  <X size={20} className="text-navy" />
                </button>
                <span className="absolute bottom-4 left-4 z-10 bg-gold text-navy text-xs font-bold uppercase px-3 py-1">{active.category}</span>
              </div>
              <div className="p-8">
                <h2 className="font-display text-3xl text-navy">{active.title}</h2>
                <div className="mt-3 flex flex-wrap gap-6 text-sm text-muted-foreground">
                  {active.location && <span className="flex items-center gap-2"><MapPin size={16} className="text-gold" /> {active.location}</span>}
                  {active.year && <span className="flex items-center gap-2"><Calendar size={16} className="text-gold" /> {active.year}</span>}
                </div>
                <p className="mt-5 text-muted-foreground leading-relaxed">{active.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CTASection />
    </PublicLayout>
  );
}
